<template>
  <div class="view-container">
    <div class="settings-header">
      <h3><i class="fas fa-cog"></i> 邮箱配置</h3>
      <p>配置 SMTP 发送服务器和 POP3 接收服务器参数</p>
    </div>

    <div class="config-cards">
      <div class="config-card">
        <div class="card-header"><h4><i class="fas fa-paper-plane"></i> SMTP 发送服务器配置</h4><span class="test-btn" @click="testSMTP">测试连接</span></div>
        <div class="card-body">
          <div class="form-grid">
            <div class="form-group"><label>SMTP 服务器地址</label><input v-model="smtp.host" class="form-control" placeholder="smtp.example.com"></div>
            <div class="form-group"><label>端口</label><input v-model="smtp.port" class="form-control" placeholder="465"></div>
            <div class="form-group"><label>邮箱账号</label><input v-model="smtp.user" class="form-control" placeholder="user@example.com"></div>
            <div class="form-group"><label>邮箱密码/授权码</label><input v-model="smtp.pass" type="password" class="form-control" placeholder="请输入密码或授权码"></div>
            <div class="form-group"><label>加密方式</label><select v-model="smtp.ssl" class="form-control"><option :value="true">SSL</option><option :value="false">STARTTLS</option></select></div>
            <div class="form-group"><label>发件人名称</label><input v-model="smtp.senderName" class="form-control" placeholder="如: 顾德智能"></div>
          </div>
          <div class="form-tip"><i class="fas fa-info-circle"></i> 常见SMTP端口: SSL-465, STARTTLS-587; QQ邮箱需使用授权码而非密码</div>
        </div>
      </div>

      <div class="config-card">
        <div class="card-header"><h4><i class="fas fa-inbox"></i> POP3 接收服务器配置</h4><span class="test-btn" @click="testPOP3">测试连接</span></div>
        <div class="card-body">
          <div class="form-grid">
            <div class="form-group"><label>POP3 服务器地址</label><input v-model="pop3.host" class="form-control" placeholder="pop.example.com"></div>
            <div class="form-group"><label>端口</label><input v-model="pop3.port" class="form-control" placeholder="995"></div>
            <div class="form-group"><label>邮箱账号</label><input v-model="pop3.user" class="form-control" placeholder="user@example.com"></div>
            <div class="form-group"><label>邮箱密码/授权码</label><input v-model="pop3.pass" type="password" class="form-control" placeholder="请输入密码或授权码"></div>
            <div class="form-group"><label>加密方式</label><select v-model="pop3.ssl" class="form-control"><option :value="true">SSL</option><option :value="false">无加密</option></select></div>
            <div class="form-group"><label>获取邮件数量</label><input v-model="pop3.fetchCount" type="number" class="form-control" placeholder="50"></div>
          </div>
          <div class="form-tip"><i class="fas fa-info-circle"></i> 常见POP3端口: SSL-995, 无加密-110; 获取数量建议不超过100封</div>
        </div>
      </div>
    </div>

    <div class="config-presets">
      <h4><i class="fas fa-bookmark"></i> 快速配置</h4>
      <div class="preset-grid">
        <div v-for="p in presets" :key="p.name" class="preset-card" @click="applyPreset(p)">
          <div class="preset-icon"><i :class="p.icon"></i></div>
          <div class="preset-name">{{ p.name }}</div>
          <div class="preset-desc">{{ p.desc }}</div>
        </div>
      </div>
    </div>

    <div class="form-actions">
      <button @click="saveConfig" class="btn btn-primary"><i class="fas fa-save"></i> 保存配置</button>
      <button @click="resetConfig" class="btn btn-default"><i class="fas fa-undo"></i> 重置</button>
    </div>

    <div v-if="showTestResult" class="test-result" :class="testSuccess ? 'success' : 'fail'">
      <i :class="testSuccess ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
      <span>{{ testMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'

const smtp = reactive({ host: 'smtp.example.com', port: '465', user: '', pass: '', ssl: true, senderName: '顾德智能' })
const pop3 = reactive({ host: 'pop.example.com', port: '995', user: '', pass: '', ssl: true, fetchCount: '50' })
const showTestResult = ref(false)
const testSuccess = ref(false)
const testMessage = ref('')

const presets = [
  { name: 'QQ 邮箱', desc: 'SMTP: smtp.qq.com\nPOP3: pop.qq.com', icon: 'fab fa-qq', smtpHost: 'smtp.qq.com', smtpPort: '465', popHost: 'pop.qq.com', popPort: '995' },
  { name: '163 邮箱', desc: 'SMTP: smtp.163.com\nPOP3: pop.163.com', icon: 'fas fa-envelope', smtpHost: 'smtp.163.com', smtpPort: '465', popHost: 'pop.163.com', popPort: '995' },
  { name: 'Gmail', desc: 'SMTP: smtp.gmail.com\nPOP3: pop.gmail.com', icon: 'fab fa-google', smtpHost: 'smtp.gmail.com', smtpPort: '587', popHost: 'pop.gmail.com', popPort: '995' },
  { name: 'Outlook', desc: 'SMTP: smtp.office365.com\nPOP3: outlook.office365.com', icon: 'fab fa-microsoft', smtpHost: 'smtp.office365.com', smtpPort: '587', popHost: 'outlook.office365.com', popPort: '995' }
]

const applyPreset = (p) => {
  smtp.host = p.smtpHost; smtp.port = p.smtpPort; pop3.host = p.popHost; pop3.port = p.popPort
  window.$message.warning('已应用 ' + p.name + ' 的服务器配置，请填写账号和密码/授权码')
}

const testSMTP = () => {
  if (!smtp.host || !smtp.user) { window.$message.warning('请先填写 SMTP 服务器信息'); return }
  showTestResult.value = true; testMessage.value = '正在连接 SMTP 服务器 ' + smtp.host + ':' + smtp.port + ' ...'
  setTimeout(() => { testSuccess.value = true; testMessage.value = 'SMTP 连接成功！服务器 ' + smtp.host + ' 响应正常。' }, 1500)
}
const testPOP3 = () => {
  if (!pop3.host || !pop3.user) { window.$message.warning('请先填写 POP3 服务器信息'); return }
  showTestResult.value = true; testMessage.value = '正在连接 POP3 服务器 ' + pop3.host + ':' + pop3.port + ' ...'
  setTimeout(() => { testSuccess.value = true; testMessage.value = 'POP3 连接成功！服务器 ' + pop3.host + ' 响应正常。' }, 1500)
}

const saveConfig = () => { window.$message.success('邮箱配置已保存！'); showTestResult.value = false }
const resetConfig = () => { smtp.host = ''; smtp.port = ''; smtp.user = ''; smtp.pass = ''; smtp.senderName = ''; pop3.host = ''; pop3.port = ''; pop3.user = ''; pop3.pass = ''; showTestResult.value = false }
</script>

<style scoped>
.view-container { padding: 24px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); max-height: calc(100vh - 110px); overflow-y: auto; }
.settings-header { margin-bottom: 24px; }
.settings-header h3 { margin: 0 0 6px 0; font-size: 18px; color: #1f2937; display: flex; align-items: center; gap: 8px; }
.settings-header p { margin: 0; font-size: 13px; color: #6b7280; }

.config-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
.config-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: #fafbfc; border-bottom: 1px solid #e5e7eb; }
.card-header h4 { margin: 0; font-size: 14px; color: #1f2937; display: flex; align-items: center; gap: 8px; }
.card-body { padding: 20px; }
.test-btn { font-size: 12px; color: #f59e0b; cursor: pointer; padding: 4px 12px; border: 1px solid #f59e0b; border-radius: 4px; transition: all 0.2s; }
.test-btn:hover { background: #f59e0b; color: #fff; }

.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.form-group { margin-bottom: 0; }
.form-group label { display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px; font-weight: 500; }
.form-group .form-control { width: 100%; padding: 8px 12px; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 13px; box-sizing: border-box; }
.form-tip { margin-top: 14px; font-size: 11px; color: #9ca3af; display: flex; align-items: center; gap: 6px; }

.config-presets { margin-bottom: 24px; }
.config-presets h4 { font-size: 14px; color: #1f2937; margin: 0 0 12px 0; display: flex; align-items: center; gap: 8px; }
.preset-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.preset-card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; cursor: pointer; text-align: center; transition: all 0.2s; }
.preset-card:hover { border-color: #f59e0b; box-shadow: 0 2px 8px rgba(245,158,11,0.15); }
.preset-icon { font-size: 28px; color: #f59e0b; margin-bottom: 8px; }
.preset-name { font-size: 13px; font-weight: 600; color: #1f2937; margin-bottom: 4px; }
.preset-desc { font-size: 10px; color: #9ca3af; white-space: pre-line; line-height: 1.4; }

.form-actions { padding-top: 16px; border-top: 1px solid #f3f4f6; display: flex; gap: 10px; }
.btn { padding: 8px 20px; border: 1px solid transparent; border-radius: 6px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.2s; }
.btn-primary { background: #f59e0b; color: #fff; border-color: #f59e0b; }
.btn-primary:hover { background: #d97706; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }

.test-result { margin-top: 16px; padding: 12px 16px; border-radius: 6px; display: flex; align-items: center; gap: 8px; font-size: 13px; }
.test-result.success { background: #dcfce7; color: #16a34a; border: 1px solid #bbf7d0; }
.test-result.fail { background: #fee2e2; color: #dc2626; border: 1px solid #fecaca; }
</style>
